import React, { useEffect, useRef, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { Formik } from 'formik'
import classNames from 'classnames'
import { ExperienceInputField } from '../form'
import QuestHOC from './QuestHOC'
import ExperienceDeleteIcon from './ExperienceDeleteIcon'
import ExperienceButton from './ExperienceButton'
import ExperienceConfirmModal from './ExperienceConfirmModal'

const FAQ_ROW_DND = 'FAQ_ROW'

const truncateLabel = (text) => {
  const t = String(text || '').trim()
  if (t.length > 72) return `${t.slice(0, 69)}\u2026`
  return t
}

const emptyFaq = () => ({ question: '', answer: '' })

const hasFaqContent = (faq) => !!(faq?.question?.trim() || faq?.answer?.trim())

const adjustEditingIndex = (dragIndex, hoverIndex, currentIndex) => {
  if (typeof currentIndex !== 'number') {
    return currentIndex
  }
  if (currentIndex === dragIndex) {
    return hoverIndex
  }
  if (dragIndex < currentIndex && hoverIndex >= currentIndex) {
    return currentIndex - 1
  }
  if (dragIndex > currentIndex && hoverIndex <= currentIndex) {
    return currentIndex + 1
  }
  return currentIndex
}

const FaqRow = ({
  faq,
  index,
  isEditing,
  onEdit,
  onDelete,
  moveItem,
  onDragEnd,
}) => {
  const ref = useRef(null)

  const [{ handlerId }, drop] = useDrop({
    accept: FAQ_ROW_DND,
    collect(monitor) {
      return { handlerId: monitor.getHandlerId() }
    },
    hover(item, monitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) {
        return
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      moveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: FAQ_ROW_DND,
    item: () => ({ index }),
    end() {
      if (typeof onDragEnd === 'function') {
        onDragEnd()
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  return (
    <div
      ref={ref}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      className={classNames('experience-lrow', {
        'is-editing': isEditing,
        'is-dragging': isDragging,
      })}
      data-handler-id={handlerId}
      role='button'
      tabIndex={0}
      onClick={() => onEdit(index)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onEdit(index)
        }
      }}>
      <span
        className='experience-grab'
        title='Reorder'
        onClick={(e) => e.stopPropagation()}>
        ⋮⋮
      </span>
      <span className='experience-lrow-copy'>
        <b>{faq.question || 'Untitled question'}</b>
        {faq.answer ? <small>{faq.answer}</small> : null}
      </span>
      <button
        type='button'
        className='experience-x'
        title='Delete'
        aria-label='Delete'
        onClick={(e) => {
          e.stopPropagation()
          onDelete(index)
        }}>
        <ExperienceDeleteIcon />
      </button>
    </div>
  )
}

const QuestFAQs = ({ questDetail, updateQuestDetails, isFormSubmitting }) => {
  const [faqs, setFaqs] = useState(
    questDetail?.faqs?.length > 0 ? questDetail.faqs : [],
  )
  const faqsRef = useRef(faqs)
  const [editingIndex, setEditingIndex] = useState(null) // number | 'new' | null
  const [draft, setDraft] = useState(emptyFaq())
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    index: null,
    label: '',
  })

  useEffect(() => {
    faqsRef.current = faqs
  }, [faqs])

  useEffect(() => {
    setFaqs(questDetail?.faqs?.length > 0 ? questDetail.faqs : [])
  }, [questDetail.faqs])

  useEffect(() => {
    if (editingIndex === null) {
      return undefined
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeEditor()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [editingIndex])

  const persist = (nextFaqs, options = {}) => {
    const cleaned = nextFaqs.filter(
      (f) => f.question?.trim() || f.answer?.trim(),
    )
    setFaqs(cleaned)
    updateQuestDetails(
      { faqs: cleaned },
      options.silent ? { silent: true } : undefined,
    )
  }

  const openEdit = (idx) => {
    if (editingIndex === idx) {
      closeEditor()
      return
    }
    setEditingIndex(idx)
    setDraft({ ...faqs[idx] })
  }

  const openAdd = () => {
    setEditingIndex('new')
    setDraft(emptyFaq())
  }

  const closeEditor = () => {
    setEditingIndex(null)
    setDraft(emptyFaq())
  }

  const handleFaqSave = (values) => {
    if (editingIndex === 'new') {
      if (!hasFaqContent(values)) {
        closeEditor()
        return
      }
      persist([...faqs, values])
    } else if (typeof editingIndex === 'number') {
      const next = [...faqs]
      next[editingIndex] = values
      persist(next)
    }
    closeEditor()
  }

  const requestRemoveAt = (idx) => {
    const faq = faqs[idx]
    setDeleteModal({
      show: true,
      index: idx,
      label: faq?.question?.trim() || 'Untitled question',
    })
  }

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, index: null, label: '' })
  }

  const confirmRemoveAt = () => {
    const idx = deleteModal.index
    closeDeleteModal()
    if (typeof idx !== 'number') return
    const next = faqs.filter((_, i) => i !== idx)
    persist(next)
    if (editingIndex === idx) closeEditor()
  }

  const moveFaq = (dragIndex, hoverIndex) => {
    const next = [...faqsRef.current]
    const [moved] = next.splice(dragIndex, 1)
    next.splice(hoverIndex, 0, moved)
    faqsRef.current = next
    setFaqs(next)

    if (typeof editingIndex === 'number') {
      const nextEditingIndex = adjustEditingIndex(
        dragIndex,
        hoverIndex,
        editingIndex,
      )
      if (nextEditingIndex !== editingIndex) {
        setEditingIndex(nextEditingIndex)
      }
    }
  }

  const persistAfterReorder = () => {
    updateQuestDetails({ faqs: faqsRef.current }, { silent: true })
  }

  const deleteName = truncateLabel(deleteModal.label)
  const deleteMessage = deleteName
    ? `\u201C${deleteName}\u201D will be removed. This question will be removed from the Prosperity page.`
    : 'This question will be removed from the Prosperity page.'

  return (
    <div>
      <div className='experience-lrow-list'>
        {faqs.map((faq, idx) => (
          <React.Fragment key={idx}>
            <FaqRow
              faq={faq}
              index={idx}
              isEditing={editingIndex === idx}
              onEdit={openEdit}
              onDelete={requestRemoveAt}
              moveItem={moveFaq}
              onDragEnd={persistAfterReorder}
            />
            {editingIndex === idx ? (
              <FaqInlineEditor
                initial={draft}
                onSave={handleFaqSave}
                onCancel={closeEditor}
                isSaving={isFormSubmitting}
              />
            ) : null}
          </React.Fragment>
        ))}
      </div>

      {editingIndex === 'new' ? (
        <FaqInlineEditor
          initial={draft}
          onSave={handleFaqSave}
          onCancel={closeEditor}
          isSaving={isFormSubmitting}
        />
      ) : null}

      {editingIndex === null ? (
        <div className='experience-form-actions'>
          <ExperienceButton variant='outline' size='sm' onClick={openAdd}>
            Add New FAQ
          </ExperienceButton>
        </div>
      ) : null}

      <ExperienceConfirmModal
        show={deleteModal.show}
        onHide={closeDeleteModal}
        onConfirm={confirmRemoveAt}
        title='Delete this FAQ?'
        message={deleteMessage}
        confirmLabel='Delete'
        isLoading={isFormSubmitting}
      />
    </div>
  )
}

const FaqInlineEditor = ({ initial, onSave, onCancel, isSaving = false }) => (
  <div className='experience-add-form'>
    <Formik
      initialValues={initial}
      enableReinitialize
      onSubmit={(values) => onSave(values)}>
      {(formik) => {
        const { handleSubmit } = formik
        return (
          <form onSubmit={handleSubmit}>
            <div className='experience-faq-editor'>
              <div className='experience-f'>
                <ExperienceInputField
                  name='question'
                  label='Question'
                  placeholder='Add question'
                  capitalizeFirstLetter
                  showErrorMessage={false}
                  displayFlex={false}
                />
              </div>
              <div className='experience-f mt-12px'>
                <ExperienceInputField
                  name='answer'
                  label='Answer'
                  placeholder='Add a short answer'
                  as='textarea'
                  rows={3}
                  capitalizeFirstLetter
                  showErrorMessage={false}
                  displayFlex={false}
                />
              </div>
            </div>
            <div className='experience-form-actions'>
              <ExperienceButton
                type='submit'
                variant='primary'
                size='sm'
                isLoading={isSaving}
                disabled={isSaving}>
                Save FAQ
              </ExperienceButton>
              <ExperienceButton
                type='button'
                variant='outline'
                size='sm'
                onClick={onCancel}
                disabled={isSaving}>
                Cancel
              </ExperienceButton>
            </div>
          </form>
        )
      }}
    </Formik>
  </div>
)

export default QuestHOC(QuestFAQs)
